import { all, call, put, select, spawn } from 'redux-saga/effects';
import { assocPath, keys, map } from 'ramda';
import { push } from 'connected-react-router';
import { normalize } from 'normalizr';
import { get } from 'object-path';

import * as schema from '../schema';

import { send } from './index';
import { getChatsSaga } from './getChatsSagas';
import { clearErrorSaga } from './helperSagas';
import * as actions from '../actions/chatActions';
import { addError } from '../actions/chatActions';

import {
  ATTACHMENT_DOWNLOAD_V1,
  ATTACHMENT_VIDEO_IMAGE_DOWNLOAD_V1,
  IDENTITY_GET_V1,
  CHAT_GET_PUBLIC_CHANNELS_V1,
  USER_SEARCH_V1,
  AUTHENTICATED,
} from '../constants/ApiEndpoints';
import { TIMEOUT_ERROR_REASON, MAX_VIDEOS_IN_MEMORY } from '../constants/AppConstants';
import * as types from '../constants/ActionTypes';
import * as AttachmentUtils from '../helpers/AttachmentUtils';
import { logError } from '../helpers/BrowserUtils';
import { getTimeoutLength } from './generatorUtils';
import { httpGet, httpGetBlob } from '../helpers/HttpUtils';

export function* searchUsersSaga(action, numRetries = 0) {
  const { pageNumber, pageSize, searchPattern, filterByPid } = action.payload;

  try {
    const response = yield httpGet(USER_SEARCH_V1, { params: { pageNumber, pageSize, searchPattern, filterByPid }, _retries: numRetries });
    const { users, totalPages } = response.data;

    yield put({ type: types.SEARCH_USERS_SUCCESS, payload: users });
    if (pageNumber < totalPages - 1) {
      action = assocPath(['payload', 'pageNumber'], pageNumber + 1, action);
      yield call(searchUsersSaga, action);
    }
    return true;
  } catch (e) {
    logError('Error Searching for Users', e);
    return false;
  }
}

const normalizePublicChannels = channels => normalize(channels, schema.publicChannels);

export function* getUnreadCountSaga(action) {
  const { count, successful, reason } = yield call(send, action);
  const { chatId } = action.payload;
  if (successful) {
    yield put({ type: types.GET_UNREAD_COUNT_SUCCESS, payload: { count, chatId } });
  } else {
    yield put(addError(action.type, reason));
  }
}

export function* getPublicChannelsSaga(action, numRetries = 0) {
  try {
    const response = yield call(httpGet, CHAT_GET_PUBLIC_CHANNELS_V1, { _retries: numRetries });
    const publicChannels = response.data;

    const { entities: payload } = yield call(normalizePublicChannels, publicChannels);

    yield put({ type: types.GET_PUBLIC_CHANNELS_SUCCESS, payload });
    return true;
  } catch (e) {
    yield put(addError(types.GET_PUBLIC_CHANNELS_FAIL, e));
    return false;
  }
}

export function* startChatSaga(action) {
  yield call(clearErrorSaga, action.type);
  
  const { chatId, successful, reason } = yield call(send, action);
  if (successful) {
    yield call(getChatsSaga, actions.getChats([chatId]));
    yield put(push(`/chat/${chatId}`));
  } else {
    yield put(addError(action.type, reason));
  }
}
/** ******************************  Hide/Show Chat Workers  ********************************* */
export function* hideShowChatSaga(action) {
  const { hidden, chatIds } = action.payload;
  // Get the type
  const type = hidden ? types.HIDE_CHAT_SUCCESS : types.SHOW_CHAT_SUCCESS;

  // Update state as successful
  yield all(map(chatId => put({ type, payload: { chatId, hidden } }), chatIds));

  // Spawn off async worker to make ws call
  yield spawn(hideShowChatWorker, action);
}

// Worker saga for hiding/showing chats
function* hideShowChatWorker(action) {
  const { successful, reason } = yield call(send, action);
  if (!successful) {
    yield put(addError(action.type, reason));
  }
}
/** ***************************************************************************************** */
/**
 * Attempts to fetch the logged in user's identity with retry logic
 * @returns number - represents generally the http status of the request
 */
export function* getIdentity() {
  const config = { timeout: getTimeoutLength(types.GET_IDENTITY) };
  const identity = yield call(httpGet, IDENTITY_GET_V1, config);
  const { data: { user, practiceName, oAuthLogoutUrl, oAuthLoginUrl } } = identity;
  yield put(actions.getIdentitySuccess(user, practiceName, oAuthLogoutUrl, oAuthLoginUrl));
  return identity;
}

export function* isAuthenticated() {
  const config = { timeout: getTimeoutLength(types.GET_IDENTITY) };
  const resp = yield call(httpGet, AUTHENTICATED, config);
  return resp.status === 200;
}

export function* markAsReadSaga(action) {
  const { chatId } = action.payload;
  const result = yield call(send, action);
  if (result) {
    const { messageId, successful, reason } = result;
    if (successful) {
      yield call(clearErrorSaga, action.type);
      yield put({ type: types.MARK_AS_READ_SUCCESS, payload: { chatId, messageId } });
    } else {
      yield put(addError(action.type, reason));
    }
  } else {
    yield put(addError(action.type, TIMEOUT_ERROR_REASON));
  }
}

export function* setTypingSaga(action) {
  const { successful, reason } = yield call(send, action);
  if (successful) {
    yield call(clearErrorSaga, action.type);
  } else {
    yield put(addError(action.type, reason));
  }
}

export function* getMessagesSaga(action) {
  const { chatId, page } = action.payload;
  const response = yield call(send, action);
  if (response) {
    const {
      messages, totalPages, totalElements, successful, reason,
    } = response;
    if (successful) {
      yield call(clearErrorSaga, action.type);

      const normalized = normalize(messages, schema.messages);

      // Fetch images only for inline rendering
      const { attachments } = normalized.entities;
      // Fetch all images in each message for real-time inline rendering
      const images = keys(attachments).filter(id => AttachmentUtils.isNonSvgImage(attachments[id]));
      yield all(map(id => put(actions.downloadAttachment(id)), images));

      // Fetch SVGs using the websocket API so we can get the image in base64 so we can in-line render it.  I do not
      // have a good explanation why SVGs need to be handled differently when rendering other than that they are of xml
      // format - unlike other images
      const svgs = keys(attachments).filter(id => AttachmentUtils.isSvgImage(attachments[id]));
      yield all(map(id => put(actions.getAttachment(id)), svgs));

      // Fetch all video preview images for inline rendering of a playable video attachment
      const videoImages = keys(attachments).filter(id => AttachmentUtils.isVideo(attachments[id]));
      yield all(map(id => put(actions.downloadVideoImageAttachment(id)), videoImages));

      yield put({ type: types.GET_MESSAGES_SUCCESS, payload: { chatId, ...normalized.entities } });
      yield put({
        type: types.GET_MESSAGES_PAGING_DATA,
        payload: { [chatId]: { totalPages, totalElements, lastRequestedPage: page } },
      });
    } else {
      yield put(addError(action.type, reason));
    }
  } else {
    yield put(addError(action.type, TIMEOUT_ERROR_REASON));
  }
}

export function* getAttachmentSaga(action) {
  const payload = yield call(send, action);
  const { successful, reason } = payload;
  if (successful) {
    yield call(clearErrorSaga, action.type);
    yield put({ type: types.GET_ATTACHMENT_SUCCESS, payload });
  } else {
    yield put(addError(action.type, reason));
  }
}

// HTTP route for downloading an attachment
export function* downloadAttachment(action) {
  const { attachmentId, isStreaming } = action.payload;
  try {
    const { data } = yield call(httpGetBlob, `${ATTACHMENT_DOWNLOAD_V1}?attachmentId=${attachmentId}`);

    yield call(clearErrorSaga, action.type);
    yield put({ type: types.DOWNLOAD_ATTACHMENT_SUCCESS, payload: { blob: data, attachmentId } });

    if (isStreaming) {
      yield put({ type: types.ADD_VIDEO, payload: { attachmentId } });

      const videos = yield select(state => state.videos);
      if (get(videos, 'length', 0) > MAX_VIDEOS_IN_MEMORY) {
        yield put({ type: types.REMOVE_VIDEO });
        yield put({ type: types.REVOKE_ATTACHMENT_URL, payload: videos[0] });
      }
    }
  } catch (e) {
    yield put({ type: types.DOWNLOAD_ATTACHMENT_FAIL, payload: { attachmentId } });
    yield put(addError(action.type, `Unable to download attachment [${attachmentId}] - ${get(e, 'response.status')} : ${get(e, 'response.statusText')}`));
  }
}

// HTTP API Call for Downloading Video Preview Image Attachments
export function* downloadVideoAttachment(action) {
  const { attachmentId } = action.payload;

  try {
    const response = yield call(httpGetBlob, `${ATTACHMENT_VIDEO_IMAGE_DOWNLOAD_V1}?attachmentId=${attachmentId}`);
    yield call(clearErrorSaga, action.type);
    yield put({ type: types.DOWNLOAD_VIDEO_IMAGE_ATTACHMENT_SUCCESS, payload: { blob: response.data, attachmentId } });
  } catch (e) {
    yield put(addError(action.type,
      `Unable to download video image attachment [${attachmentId}] - ${get(e, 'response.status')} : ${get(e, 'response.statusText')}`));
  }
}

export function* joinChannelSaga(action) {
  const { chatId } = action.payload;
  const { successful, reason } = yield call(send, action);
  if (successful) {
    yield call(clearErrorSaga, action.type);
    yield put({ type: types.JOIN_CHANNEL_SUCCESS, payload: { chatId } });
    yield call(getChatsSaga, actions.getChats([chatId]));
    yield call(getMessagesSaga, actions.getMessages(chatId));
    yield put(push(`/chat/${chatId}`));
  } else {
    yield put(addError(action.type, reason));
  }
}

export function* leaveChannelSaga(action) {
  const { chatId } = action.payload;
  const { successful, reason } = yield call(send, action);
  if (successful) {
    yield call(clearErrorSaga, action.type);
    yield put({ type: types.LEAVE_CHANNEL_SUCCESS, payload: { chatId } });
  } else {
    yield put(addError(action.type, reason));
  }
}

export function* addUsersToChannelSaga(action) {
  const { chatId } = action.payload;
  const { successful, reason } = yield call(send, action);
  if (successful) {
    yield call(clearErrorSaga, action.type);
    yield put(push(`/chat/${chatId}/details`));
  } else {
    yield put(addError(action.type, reason));
  }
}
